import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  useToast,
  useColorModeValue,
  IconButton,
  FormControl,
  FormLabel,
  Center,
  Image,
  RadioGroup,
  Radio,
  Stack
} from '@chakra-ui/react';
import { FiArrowLeft, FiUpload } from 'react-icons/fi';
import IconWrapper from '../components/IconWrapper';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';

const CreateSeason: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { seriesId } = useParams<{ seriesId: string }>();
  const toast = useToast();
  
  const [seasonNumber, setSeasonNumber] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [posterOption, setPosterOption] = useState<'reuse' | 'new'>('reuse');
  // const [posterFile, setPosterFile] = useState<File | null>(null);
  const [posterPreview, setPosterPreview] = useState<string | null>(null);
  const [seriesPoster, setSeriesPoster] = useState<string>('');
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const inputBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.900', 'white');
  const labelColor = useColorModeValue('gray.600', 'gray.400');

  // Load series poster on component mount
  React.useEffect(() => {
    if (seriesId) {
      const seriesData = localStorage.getItem(`series-${seriesId}`);
      if (seriesData) {
        const series = JSON.parse(seriesData);
        setSeriesPoster(series.poster || '');
      }
    }
  }, [seriesId]);

  const handlePosterSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Invalid file type',
          description: 'Please select an image file.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      
      // setPosterFile(file);
      setPosterPreview(URL.createObjectURL(file));
    }
  };

  const handleCreateSeason = async () => {
    if (!seasonNumber.trim()) {
      toast({
        title: 'Season number required',
        description: 'Please enter a season number.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsCreating(true);
    
    try {
      // Wait for the function to be available with a timeout
      let attempts = 0;
      const maxAttempts = 10;
      
      while (!(window as any).addSeasonToSeries && attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }
      
      if ((window as any).addSeasonToSeries) {
        (window as any).addSeasonToSeries(seasonNumber);
        
        toast({
          title: 'Season created successfully!',
          description: `Season ${seasonNumber} has been created.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        // Navigate back to seasons management
        navigate(`/series/${seriesId}/manage`);
      } else {
        // Fallback: create season directly in localStorage
        const seasonData = {
          id: `season-${Date.now()}`,
          title: `Season ${seasonNumber}`,
          seasonNumber: seasonNumber,
          poster: posterOption === 'reuse' ? seriesPoster : (posterPreview || ''),
          episodeCount: 0,
          description: ''
        };
        
        const existingSeasons = JSON.parse(localStorage.getItem(`seasons-${seriesId}`) || '[]');
        const updatedSeasons = [...existingSeasons, seasonData];
        localStorage.setItem(`seasons-${seriesId}`, JSON.stringify(updatedSeasons));
        
        toast({
          title: 'Season created successfully!',
          description: `Season ${seasonNumber} has been created.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        // Navigate back to seasons management
        navigate(`/series/${seriesId}/manage`);
      }
    } catch (error) {
      console.error('Error creating season:', error);
      toast({
        title: 'Error',
        description: 'Failed to create season. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsCreating(false);
    }
  };

  if (!user) {
    return (
      <Center h="50vh">
        <VStack gap={4}>
          <Text fontSize="lg" color="gray.500">
            Please log in to create seasons.
          </Text>
          <Button onClick={() => navigate('/login')} colorScheme="purple">
            Log In
          </Button>
        </VStack>
      </Center>
    );
  }

  return (
    <Box minH="100vh" bg={bgColor}>
      {/* Header */}
      <Box p={4} borderBottom="1px solid" borderColor={borderColor}>
        <HStack gap={4} align="center">
          <IconButton
            aria-label="Go back"
            icon={<IconWrapper icon={FiArrowLeft} size={20} />}
            variant="ghost"
            onClick={() => navigate(-1)}
            size="sm"
            bg="purple.100"
            _hover={{ bg: 'purple.200' }}
          />
          <Text fontSize="xl" fontWeight="bold" color={textColor}>
            Create Season
          </Text>
        </HStack>
      </Box>

      {/* Main Content */}
      <Box p={6} maxW="md" mx="auto">
        <VStack spacing={6} align="stretch">
          <FormControl>
            <FormLabel color={labelColor} fontWeight="bold" fontSize="md">
              Season Number
            </FormLabel>
            <Input
              value={seasonNumber}
              onChange={(e) => setSeasonNumber(e.target.value)}
              placeholder="Enter season number (e.g., 1, 2, 3)"
              bg={inputBg}
              borderColor={borderColor}
              _hover={{ borderColor: 'purple.300' }}
              _focus={{ borderColor: 'purple.500', boxShadow: '0 0 0 1px purple.500' }}
            />
          </FormControl>

          <FormControl>
            <FormLabel color={labelColor} fontWeight="bold" fontSize="md">
              Season Poster
            </FormLabel>
            <RadioGroup value={posterOption} onChange={(value) => setPosterOption(value as 'reuse' | 'new')}>
              <Stack direction="column" gap={3}>
                <Radio value="reuse" colorScheme="purple">
                  <VStack align="start" gap={2}>
                    <Text color={textColor}>Reuse series poster</Text>
                    {seriesPoster && (
                      <Image
                        src={seriesPoster}
                        alt="Series poster"
                        w="100px"
                        h="60px"
                        objectFit="cover"
                        borderRadius="md"
                        border="1px solid"
                        borderColor={borderColor}
                      />
                    )}
                  </VStack>
                </Radio>
                
                <Radio value="new" colorScheme="purple">
                  <VStack align="start" gap={2}>
                    <Text color={textColor}>Upload new poster</Text>
                    <HStack gap={3}>
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={handlePosterSelect}
                        display="none"
                        id="poster-upload"
                      />
                      <Button
                        as="label"
                        htmlFor="poster-upload"
                        leftIcon={<IconWrapper icon={FiUpload} size={16} />}
                        variant="outline"
                        cursor="pointer"
                        size="sm"
                      >
                        Select Image
                      </Button>
                      {posterPreview && (
                        <Image
                          src={posterPreview}
                          alt="Poster preview"
                          w="100px"
                          h="60px"
                          objectFit="cover"
                          borderRadius="md"
                          border="1px solid"
                          borderColor={borderColor}
                        />
                      )}
                    </HStack>
                  </VStack>
                </Radio>
              </Stack>
            </RadioGroup>
          </FormControl>

          <Button
            colorScheme="purple"
            size="lg"
            onClick={handleCreateSeason}
            isLoading={isCreating}
            loadingText="Creating..."
            isDisabled={!seasonNumber.trim()}
            w="100%"
            h="60px"
          >
            Create Season
          </Button>
        </VStack>
      </Box>
    </Box>
  );
};

export default CreateSeason;
